import { useState, useEffect, useCallback } from 'react';
import { adminSelectionService } from '../service/adminSelectionService.mjs';

export const useUsers = () => {
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const loadUsers = useCallback(async (page = 1, role = null) => {
        try {
            setLoading(true);
            setError(null);
            
            let response;
            if (role) {
                response = await adminSelectionService.getUsersByRole(role);
                setUsers(response.data.users);
                setPagination(null);
            } else {
                response = await adminSelectionService.getUsers(page, 10);
                setUsers(response.data.users);
                setPagination(response.data.pagination);
            }
            
            setCurrentPage(page);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load users');
        } finally { setLoading(false);
        }
    }, []);

    const searchUsers = useCallback(async (query) => {
        if (!query.trim()) {
            loadUsers(1);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await adminSelectionService.searchUsers(query);
            setUsers(response.data.users);
            setPagination(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Search failed');
        } finally {
            setLoading(false);
        }
    }, [loadUsers]);

    const promoteUser = useCallback(async (userId) => {
        try {
            const response = await adminSelectionService.promoteUser(userId);
            if (response.success) {
                // Reload current view
                loadUsers(currentPage);
                return response;
            }
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Failed to promote user');
        }
    }, [currentPage, loadUsers]);

    const demoteUser = useCallback(async (userId) => {
        try {
            const response = await adminSelectionService.demoteUser(userId);
            if (response.success) {
                // Reload current view
                loadUsers(currentPage);
                return response;
            }
        } catch (err) {
            throw new Error(err.response?.data?.message || 'Failed to demote user');
        }
    }, [currentPage, loadUsers]);

    useEffect(() => {
        loadUsers(1);
    }, [loadUsers]);

    return {
        users,
        pagination,
        loading,
        error,
        currentPage,
        loadUsers,
        searchUsers,
        promoteUser,
        demoteUser,
        setCurrentPage
    };
};


